/**
 * Route guard that allows access to both server admins and observatory admins/owners.
 * Use this for features that should be accessible to users who manage observatories,
 * not just server administrators.
 */
import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { hasObservatoryAdminCapabilities, hasLegacyAdminRole } from '../../utils/userCapabilities';

const AdminOrObservatoryAdminRoute = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        setLoading(true);

        // Server admins have access to everything
        if (hasLegacyAdminRole(user)) {
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // For non-server admins, check if they have observatory admin capabilities
        if (user?.id || user?._id) {
          const userId = user.id || user._id;
          const response = await apiService.getUserMemberships(userId);

          if (response?.success) {
            const userMemberships = response.data || [];

            // Check if user has admin capabilities on ANY observatory
            const canAdmin = hasObservatoryAdminCapabilities(userMemberships);
            setHasAccess(canAdmin);
          }
        }
      } catch (error) {
        console.error('Error checking admin access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            maxWidth: 400,
          }}
        >
          <LockIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            This page requires observatory administrator privileges.
            You need to be an admin or owner of at least one observatory.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};

export default AdminOrObservatoryAdminRoute;
